import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ChevronRight } from 'lucide-react';
import Header from './components/Header11';
import Footer from './components/Footer11';

const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Silk Evening Gown',
            price: 8999,
            quantity: 1,
            size: 'M',
            image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400',
        },
        {
            id: 2,
            name: 'Cashmere Blazer',
            price: 15999,
            quantity: 1,
            size: 'L',
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
        },
        {
            id: 3,
            name: 'Leather Clutch',
            price: 4999,
            quantity: 1,
            size: 'One Size',
            image: 'https://images.unsplash.com/photo-1566150905458-1bf1fc113f0d?w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(items =>
            items.map(item =>
                item.id === id
                    ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                    : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems(items => items.filter(item => item.id !== id));
    };

    const applyCoupon = () => {
        if (couponCode.toUpperCase() === 'LUXURY10') {
            setAppliedCoupon({ code: 'LUXURY10', discount: 10 });
        } else {
            alert('Invalid coupon code');
        }
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discount = appliedCoupon ? (subtotal * appliedCoupon.discount) / 100 : 0;
    const shipping = subtotal > 5000 ? 0 : 200;
    const total = subtotal - discount + shipping;

    return (
        <div className="min-h-screen bg-gray-900 text-white font-serif">
            <Header showBackButton={true} cartCount={cartItems.length} />

            <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
                <div className="text-center mb-12">
                    <p className="text-sm tracking-widest mb-4 text-white/70">YOUR SELECTION</p>
                    <h1 className="text-5xl lg:text-6xl font-light">Shopping Cart</h1>
                </div>

                {cartItems.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-white/40 mb-6">
                            <svg className="w-24 h-24 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <h2 className="text-3xl font-light mb-4">Your cart is empty</h2>
                        <p className="text-white/60 mb-8">Explore our curated collection</p>
                        <button
                            onClick={() => navigate('/template11/shop')}
                            className="group inline-flex items-center gap-3 border-b border-white/30 hover:border-white pb-2 transition-all"
                        >
                            <span className="text-sm tracking-widest">CONTINUE SHOPPING</span>
                            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-6">
                            {cartItems.map(item => (
                                <div key={item.id} className="flex gap-6 pb-6 border-b border-gray-700">
                                    {/* Image */}
                                    <div className="w-32 h-40 flex-shrink-0 bg-gray-800 overflow-hidden">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1">
                                        <h3 className="text-xl font-light mb-2">{item.name}</h3>
                                        <p className="text-sm text-white/60 mb-4">Size: {item.size}</p>
                                        <p className="text-lg">₹{item.price.toLocaleString()}</p>
                                    </div>

                                    {/* Quantity Controls */}
                                    <div className="flex flex-col justify-between items-end">
                                        <button
                                            onClick={() => removeItem(item.id)}
                                            className="text-white/40 hover:text-white transition-colors"
                                        >
                                            <Trash2 size={18} strokeWidth={1.5} />
                                        </button>

                                        <div className="flex items-center gap-4 border border-gray-700">
                                            <button
                                                onClick={() => updateQuantity(item.id, -1)}
                                                className="w-10 h-10 flex items-center justify-center hover:bg-gray-800 transition-colors"
                                            >
                                                <Minus size={14} strokeWidth={1.5} />
                                            </button>
                                            <span className="w-8 text-center font-light">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.id, 1)}
                                                className="w-10 h-10 flex items-center justify-center hover:bg-gray-800 transition-colors"
                                            >
                                                <Plus size={14} strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="border border-gray-700 p-8 sticky top-24">
                                <h2 className="text-2xl font-light mb-8 tracking-wide">Order Summary</h2>

                                {/* Coupon Code */}
                                <div className="mb-8">
                                    <label className="block text-sm font-light text-white/70 mb-3 tracking-wider">
                                        PROMO CODE
                                    </label>
                                    <div className="flex gap-3">
                                        <input
                                            type="text"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            placeholder="Enter code"
                                            className="flex-1 px-4 py-3 bg-transparent border border-gray-700 text-white placeholder-white/30 focus:outline-none focus:border-white/50 transition-colors"
                                        />
                                        <button
                                            onClick={applyCoupon}
                                            className="px-4 py-3 border border-gray-700 hover:border-white/50 transition-colors"
                                        >
                                            <Tag size={18} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                    {appliedCoupon && (
                                        <p className="text-sm text-white/70 mt-3">
                                            {appliedCoupon.code} applied • {appliedCoupon.discount}% off
                                        </p>
                                    )}
                                    <p className="text-xs text-white/40 mt-3">Try: LUXURY10 for 10% off</p>
                                </div>

                                {/* Price Breakdown */}
                                <div className="space-y-4 mb-8 pb-8 border-b border-gray-700">
                                    <div className="flex justify-between text-white/70 font-light">
                                        <span className="tracking-wide">Subtotal</span>
                                        <span>₹{subtotal.toLocaleString()}</span>
                                    </div>
                                    {appliedCoupon && (
                                        <div className="flex justify-between text-white/70 font-light">
                                            <span className="tracking-wide">Discount</span>
                                            <span>-₹{discount.toLocaleString()}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-white/70 font-light">
                                        <span className="tracking-wide">Shipping</span>
                                        <span>{shipping === 0 ? 'Complimentary' : `₹${shipping}`}</span>
                                    </div>
                                    {shipping === 0 && (
                                        <p className="text-xs text-white/40">Complimentary shipping on orders over ₹5000</p>
                                    )}
                                </div>

                                {/* Total */}
                                <div className="flex justify-between text-2xl font-light mb-8">
                                    <span className="tracking-wide">Total</span>
                                    <span>₹{total.toLocaleString()}</span>
                                </div>

                                {/* Checkout Button */}
                                <button
                                    onClick={() => navigate('/template11/checkout')}
                                    className="w-full group py-4 bg-white text-gray-900 font-light tracking-widest text-sm hover:bg-gray-100 transition-colors mb-4 flex items-center justify-center gap-3"
                                >
                                    PROCEED TO CHECKOUT
                                    <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </button>

                                <button
                                    onClick={() => navigate('/template11/shop')}
                                    className="w-full text-center text-white/60 hover:text-white text-sm tracking-wide transition-colors"
                                >
                                    Continue Shopping
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Cart;
